// Shared sensor-severity aggregation, used by both ApplicationsPage's dashboard
// cards and the Sidebar's per-category nav indicators, so the two stay in sync
// by construction rather than by two independently-maintained copies.
import { formatVoltage, formatFeet } from '@/lib/formatters';
import {
  resolveOnlineState, isIntrusionZone, isWaterZone,
  isSmokeCoZone, isTemperatureZone, isMiscSirenZone, isMiscNoSirenZone,
} from '@/lib/enum-resolvers';
import type { NASensorDataObject } from '@/api/models';

export interface SensorMetrics {
  // Security zones
  totalZones: number;
  zonesAlarming: number;
  zonesBypassed: number;
  zonesOnline: number;
  securitySummary: string;
  securityStatus: 'normal' | 'warning' | 'danger';

  // DC banks
  totalDCBanks: number;
  dcAlerting: number;
  dcCritical: number;
  dcSummaryLines: { label: string; voltage: string; status: 'normal' | 'warning' | 'danger' }[];
  dcStatus: 'normal' | 'warning' | 'danger';

  // AC
  totalAC: number;
  acConnected: number;
  acAlerting: number;
  acSummary: string;
  acStatus: 'normal' | 'warning' | 'danger';

  // Bilge / Nevata / Switches
  totalBilge: number;
  switchesActive: number;
  nevataAlerting: number;
  bilgeSummary: string;
  bilgeStatus: 'normal' | 'warning' | 'danger';

  // Device (single, built-in) Geofence
  totalGeofences: number;
  geofenceAlerting: number;
  geofenceEnabled: boolean;
  geofenceSummary: string;
  geofenceStatus: 'normal' | 'warning' | 'danger';

  // Global (user-created) Geofences -- no mobile equivalent exists. Being
  // outside a boundary is expected/normal for a vessel underway, not an
  // alert condition, so this deliberately has no *Status field and never
  // feeds the app-wide (sidebar) alert indicator. A vessel is typically
  // outside most of its configured geofences at any given moment (they're
  // usually centered on different locations), so "inside" means inside AT
  // LEAST ONE, not all of them -- globalGeofencesInside is that count.
  totalGlobalGeofences: number;
  globalGeofencesOutside: number;
  globalGeofencesInside: number;

  // Water zones (from security sensor list)
  hasWaterZones: boolean;

  // Smoke/CO, Temperature, Misc & Siren, Misc (No Siren) zones — each a
  // distinct DeviceCategory screen on mobile, all drawn from the same
  // securitySensorlist as Intrusion Zones (see updateSmokeCOCategory /
  // updateTemperatureCategory / updateMiscSirenZoneCategory /
  // updateMiscNoSirenZoneCategory in mobile's DeviceCategoryMetrics.cs)
  smokeCoTotal: number; smokeCoAlarming: number; smokeCoOnline: number; smokeCoStatus: 'normal' | 'warning' | 'danger';
  temperatureTotal: number; temperatureAlarming: number; temperatureOnline: number; temperatureStatus: 'normal' | 'warning' | 'danger';
  miscSirenTotal: number; miscSirenAlarming: number; miscSirenOnline: number; miscSirenStatus: 'normal' | 'warning' | 'danger';
  miscTotal: number; miscAlarming: number; miscOnline: number; miscStatus: 'normal' | 'warning' | 'danger';
}

export function computeMetrics(sd: NASensorDataObject | null | undefined): SensorMetrics {
  const empty: SensorMetrics = {
    totalZones: 0, zonesAlarming: 0, zonesBypassed: 0, zonesOnline: 0,
    securitySummary: '', securityStatus: 'normal',
    totalDCBanks: 0, dcAlerting: 0, dcCritical: 0, dcSummaryLines: [], dcStatus: 'normal',
    totalAC: 0, acConnected: 0, acAlerting: 0, acSummary: '', acStatus: 'normal',
    totalBilge: 0, switchesActive: 0, nevataAlerting: 0, bilgeSummary: '', bilgeStatus: 'normal', hasWaterZones: false,
    totalGeofences: 0, geofenceAlerting: 0, geofenceEnabled: false, geofenceSummary: '', geofenceStatus: 'normal',
    totalGlobalGeofences: 0, globalGeofencesOutside: 0, globalGeofencesInside: 0,
    smokeCoTotal: 0, smokeCoAlarming: 0, smokeCoOnline: 0, smokeCoStatus: 'normal',
    temperatureTotal: 0, temperatureAlarming: 0, temperatureOnline: 0, temperatureStatus: 'normal',
    miscSirenTotal: 0, miscSirenAlarming: 0, miscSirenOnline: 0, miscSirenStatus: 'normal',
    miscTotal: 0, miscAlarming: 0, miscOnline: 0, miscStatus: 'normal',
  };
  if (!sd) return empty;

  // Security zones — only count intrusion zones (not water or system)
  const allZones = sd.securitySensorlist ?? [];
  const zones = allZones.filter((z) => isIntrusionZone(z));
  const waterZones = allZones.filter((z) => isWaterZone(z.alarmType));
  const smokeCoZones = allZones.filter((z) => isSmokeCoZone(z));
  const temperatureZones = allZones.filter((z) => isTemperatureZone(z));
  const miscSirenZones = allZones.filter((z) => isMiscSirenZone(z));
  const miscZones = allZones.filter((z) => isMiscNoSirenZone(z));
  const smokeCoAlarming = smokeCoZones.filter((z) => z.alarmPresent).length;
  const smokeCoOnline = smokeCoZones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
  const temperatureAlarming = temperatureZones.filter((z) => z.alarmPresent).length;
  const temperatureOnline = temperatureZones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
  const miscSirenAlarming = miscSirenZones.filter((z) => z.alarmPresent).length;
  const miscSirenOnline = miscSirenZones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
  const miscAlarming = miscZones.filter((z) => z.alarmPresent).length;
  const miscOnline = miscZones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
  const zonesAlarming = zones.filter((z) => z.alarmPresent).length;
  const zonesBypassed = zones.filter((z) => z.isBypassed).length;
  const zonesOnline = zones.filter((z) => resolveOnlineState(z.onlineState) === 'Online').length;
  let securitySummary = `${zonesOnline} of ${zones.length} online`;
  let securityStatus: 'normal' | 'warning' | 'danger' = 'normal';
  if (zonesAlarming > 0) {
    securitySummary = `${zonesAlarming} zone${zonesAlarming > 1 ? 's' : ''} alarming`;
    securityStatus = 'danger';
  } else if (zonesBypassed > 0) {
    securitySummary = `${zonesBypassed} bypassed, ${zonesOnline} online`;
    securityStatus = 'warning';
  }

  // DC banks
  const dcBanks = sd.dcSensorList ?? [];
  const dcAlerting = dcBanks.filter((b) => b.alertPresent).length;
  const dcCritical = dcBanks.filter((b) => b.criticalAlertPresent).length;
  const dcSummaryLines = dcBanks.map((b) => ({
    label: b.sensorName || `Bank ${b.dcBankNum}`,
    voltage: formatVoltage(b.dcVoltage),
    status: (b.criticalAlertPresent ? 'danger' : b.alertPresent ? 'warning' : 'normal') as 'normal' | 'warning' | 'danger',
  }));
  const dcStatus: 'normal' | 'warning' | 'danger' = dcCritical > 0 ? 'danger' : dcAlerting > 0 ? 'warning' : 'normal';

  // AC
  const acList = sd.acSensorList ?? [];
  const acConnected = acList.filter((a) => a.acEnabled && a.sensorPresent).length;
  const acAlerting = acList.filter((a) => a.alertPresent).length;
  const acStatus: 'normal' | 'warning' | 'danger' = acAlerting > 0 ? 'danger' : 'normal';
  const acSummary = acConnected > 0
    ? `${acConnected} connected${acAlerting > 0 ? `, ${acAlerting} alert` : ''}`
    : 'Disconnected';

  // Bilge / Switches / Nevata / Water zones
  const switches = sd.switchSensorList ?? [];
  const nevatas = sd.nevataSensorList ?? [];
  const switchesActive = switches.filter((s) => s.isSwitchOn).length;
  const nevataAlerting = nevatas.filter((n) => n.alertState !== 'ALERT_STATE_NORMAL').length;
  const waterZoneAlerting = waterZones.filter((z) => z.alarmPresent).length;
  const totalBilge = switches.length + nevatas.length + waterZones.length;
  let bilgeSummary: string;
  let bilgeStatus: 'normal' | 'warning' | 'danger' = 'normal';
  if (waterZoneAlerting > 0) {
    bilgeSummary = `${waterZoneAlerting} water zone alert${waterZoneAlerting > 1 ? 's' : ''}`;
    bilgeStatus = 'danger';
  } else if (nevataAlerting > 0) {
    bilgeSummary = `${nevataAlerting} alert${nevataAlerting > 1 ? 's' : ''}`;
    bilgeStatus = 'danger';
  } else if (switchesActive > 0) {
    bilgeSummary = `${switchesActive} switch${switchesActive > 1 ? 'es' : ''} active`;
    bilgeStatus = 'warning';
  } else {
    bilgeSummary = `${totalBilge} sensor${totalBilge !== 1 ? 's' : ''} normal`;
  }

  // Device (single, built-in) Geofence
  const geos = sd.geofenceList ?? [];
  const geofenceAlerting = geos.filter((g) => g.alertPresent).length;
  const geofenceEnabled = geos.some((g) => g.enabled);
  const geofenceStatus: 'normal' | 'warning' | 'danger' = geofenceAlerting > 0 ? 'danger' : 'normal';
  let geofenceSummary = geofenceEnabled ? 'Armed' : 'Disabled';
  if (geofenceAlerting > 0) geofenceSummary = 'Alert Active';
  if (geos.length > 0 && geos[0].displacementDataValid) {
    geofenceSummary += ` (${formatFeet(geos[0].displacementFeet)} from center)`;
  }

  // Global (user-created) Geofences
  const globalGeos = sd.globalGeofenceList ?? [];
  const globalGeofencesOutside = globalGeos.filter((g) => g.outsideGeofence).length;
  const globalGeofencesInside = globalGeos.length - globalGeofencesOutside;

  return {
    totalZones: zones.length, zonesAlarming, zonesBypassed, zonesOnline, securitySummary, securityStatus,
    totalDCBanks: dcBanks.length, dcAlerting, dcCritical, dcSummaryLines, dcStatus,
    totalAC: acList.length, acConnected, acAlerting, acSummary, acStatus,
    totalBilge, switchesActive, nevataAlerting, bilgeSummary, bilgeStatus, hasWaterZones: waterZones.length > 0,
    totalGeofences: geos.length, geofenceAlerting, geofenceEnabled, geofenceSummary, geofenceStatus,
    totalGlobalGeofences: globalGeos.length, globalGeofencesOutside, globalGeofencesInside,
    smokeCoTotal: smokeCoZones.length, smokeCoAlarming, smokeCoOnline,
    smokeCoStatus: smokeCoAlarming > 0 ? 'danger' : 'normal',
    temperatureTotal: temperatureZones.length, temperatureAlarming, temperatureOnline,
    temperatureStatus: temperatureAlarming > 0 ? 'danger' : 'normal',
    miscSirenTotal: miscSirenZones.length, miscSirenAlarming, miscSirenOnline,
    miscSirenStatus: miscSirenAlarming > 0 ? 'danger' : 'normal',
    miscTotal: miscZones.length, miscAlarming, miscOnline,
    miscStatus: miscAlarming > 0 ? 'danger' : 'normal',
  };
}

export const statusColors = {
  normal: {
    bg: 'bg-green-50 dark:bg-green-900/10',
    border: 'border-green-200 dark:border-green-800/30',
    dot: 'bg-green-500',
    text: 'text-green-700 dark:text-green-400',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/10',
    border: 'border-yellow-200 dark:border-yellow-800/30',
    dot: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
  },
  danger: {
    bg: 'bg-red-50 dark:bg-red-900/10',
    border: 'border-red-200 dark:border-red-800/30',
    dot: 'bg-red-500 animate-pulse',
    text: 'text-red-700 dark:text-red-400',
  },
  // Informational, not an alert -- e.g. "outside a global geofence boundary"
  // is expected/normal for a vessel underway, not something to flag.
  neutral: {
    bg: 'bg-slate-50 dark:bg-navy-800',
    border: 'border-slate-200 dark:border-navy-700',
    dot: 'bg-slate-400',
    text: 'text-slate-600 dark:text-slate-400',
  },
} as const;
